import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { DataPerformanceSummaryType } from 'webapp/enums/enums';
import { equal } from 'webapp/utils/common';

import type DataPerformanceSummaryBlock from 'webapp/models-custom/data-performance-summary/block';
import type DataPerformanceSummaryDynamic from 'webapp/models-custom/data-performance-summary/dynamic';

import type {
	DataPerformanceSummary as DataPerformanceSummaryModel,
	DataPerformanceSummaryObject,
} from 'webapp/services/model-manager/data-performance-summaries';

export default abstract class DataPerformanceSummaryBase<T> extends Component<T> {
	@service('model-manager/data-performance-summaries')
	declare mmDataPerformanceSummariesService: Service.ModelManager.DataPerformanceSummaries;

	abstract get isDataIncomplete(): boolean;
	abstract get isDataEmpty(): boolean;
	abstract get object(): DataPerformanceSummaryObject | null;

	get isDataIncompleteOrEmpty() {
		return this.isDataIncomplete || this.isDataEmpty;
	}

	private _model: DataPerformanceSummaryModel | null = null;
	@cached
	get model() {
		let model = this._model;
		const object = this.object;

		if (!model || this.checkIfArgsChanged()) {
			model = object ? this.mmDataPerformanceSummariesService.createModel(object) : null;

			// eslint-disable-next-line ember/no-side-effects
			this._model = model;
		}

		return model;
	}

	private checkIfArgsChanged() {
		// objectType is not gonna change, only other args
		if (!this.object) {
			return false;
		}

		let changed = false;

		if (this.object.objectType === DataPerformanceSummaryType.DYNAMIC) {
			const object = this.object,
				objectMS = object.dataSelection?.metricSetup,
				objectDRF = object.dataSelection?.dateRange,
				model = this._model as DataPerformanceSummaryDynamic,
				modelMS = model.dataSelection?.metricSetup,
				modelDR = model.objectDateRange;

			return (
				objectMS?.key !== modelMS?.key ||
				objectMS?.spaceAccessId !== modelMS?.spaceAccessId ||
				!equal(objectMS?.dimensions, modelMS?.dimensions) ||
				!equal(objectMS?.filters, modelMS?.filters) ||
				!objectDRF ||
				!modelDR?.isEqual(objectDRF)
			);
		} else {
			changed = this.object.objectId !== this._model?.objectId;
		}

		return changed;
	}

	@action
	resetTrend(): void {
		const model = this.model;

		if (this.isDataIncompleteOrEmpty && (model?.isTypeBlock || model?.isTypeDynamic)) {
			(
				this.model as DataPerformanceSummaryBlock | DataPerformanceSummaryDynamic
			).resetTrend();
		}
	}
}
